import React, { useEffect, useRef, useState } from 'react';
import { sendLoginTotpEmailCode, checkLoginTotpEmailCode, startLoginTotpSetup, finishLoginTotpSetup, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { Qr, group } from './KernelTotpSection';
import './kernelTotp.css';

/**
 * Configurazione obbligatoria dell'autenticazione a due fattori durante il login: l'azienda l'ha attiva e
 * l'utente non l'ha ancora collegata. Flusso: codice ricevuto via email (conferma che la casella è sua) → QR (o chiave) →
 * primo codice → codici di recupero → accesso.
 * `challenge` è quello ricevuto dal login; `onDone(risposta)` completa l'accesso dopo che i codici sono stati salvati.
 */
function KernelTotpLoginSetup({ challenge, onDone, onBack, onError }) {
  const { t } = useLanguage();
  const [stage, setStage] = useState('email'); // email | app
  const [active, setActive] = useState(challenge); // sfida in uso: dopo la conferma dell'email ne arriva una nuova
  const [maskedEmail, setMaskedEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const sentRef = useRef(false);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const sendEmailCode = async () => {
    onError('');
    setBusy(true);
    try {
      const res = await sendLoginTotpEmailCode(challenge);
      setMaskedEmail(res.email || '');
    } catch (err) {
      onError(errorMessage(err, t('totp.error')));
    } finally {
      setBusy(false);
    }
  };

  // Il codice via email parte una sola volta all'apertura della schermata (il ref evita il doppio invio in sviluppo).
  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    sendEmailCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmEmail = async (e) => {
    e.preventDefault();
    onError('');
    setBusy(true);
    try {
      const res = await checkLoginTotpEmailCode(challenge, emailCode.trim());
      setActive(res.challenge);
      setStage('app');
    } catch (err) {
      onError(errorMessage(err, t('totp.error')));
    } finally {
      setBusy(false);
    }
  };

  // Email confermata: ora si può preparare il collegamento dell'app (QR e chiave).
  useEffect(() => {
    if (stage !== 'app') return undefined;
    let cancelled = false;
    startLoginTotpSetup(active)
      .then((res) => { if (!cancelled) setSetup(res); })
      .catch((err) => { if (!cancelled) onError(errorMessage(err, t('totp.error'))); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, active]);

  const confirm = async (e) => {
    e.preventDefault();
    onError('');
    setBusy(true);
    try {
      setResult(await finishLoginTotpSetup(active, code.trim()));
    } catch (err) {
      onError(errorMessage(err, t('totp.error')));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      onError(t('totp.copyError'));
    }
  };

  const download = () => {
    const blob = new Blob([`${t('totp.recoveryFileTitle')}\n\n${result.recovery_codes.join('\n')}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'codici-di-recupero.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (result) {
    const codes = result.recovery_codes || [];
    return (
      <>
        <div className="success-message">{t('totp.codesTitle')}</div>
        <p>{t('totp.codesHint')}</p>
        <div className="totp-codes">{codes.map((c) => <code key={c}>{c}</code>)}</div>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={() => copy(codes.join('\n'))}>{copied ? t('totp.copied') : t('totp.copyCodes')}</button>
          <button type="button" className="btn btn-secondary" onClick={download}>{t('totp.download')}</button>
        </div>
        <button type="button" className="btn btn-lg" onClick={() => onDone(result)}>{t('auth.totpSetupContinue')}</button>
      </>
    );
  }

  if (stage === 'email') {
    return (
      <>
        <h2>{t('auth.totpEmailTitle')}</h2>
        <p className="reset-hint">{t('auth.totpSetupHint')}</p>
        <p className="reset-hint">{maskedEmail ? t('auth.totpEmailHint', { email: maskedEmail }) : t('auth.totpEmailSending')}</p>
        <form onSubmit={confirmEmail}>
          <div className="form-group">
            <label htmlFor="login-totp-email">{t('auth.totpEmailLabel')}</label>
            <input id="login-totp-email" value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={6} placeholder="000000" autoComplete="one-time-code" autoFocus required />
          </div>
          <button type="submit" className="btn btn-lg" disabled={busy || emailCode.length !== 6}>{t('auth.totpEmailContinue')}</button>
        </form>
        <div className="login-links">
          <button type="button" className="link-button" onClick={sendEmailCode} disabled={busy}>{t('auth.totpEmailResend')}</button>
          <button type="button" className="link-button" onClick={onBack}>{t('auth.otpBack')}</button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2>{t('auth.totpSetupTitle')}</h2>
      <p className="reset-hint">{t('auth.totpSetupHint')}</p>
      {setup && (
        <form onSubmit={confirm}>
          <ol className="totp-steps">
            <li>{t('totp.step1')}</li>
            <li>
              {t('totp.step2')}
              <div className="totp-scan">
                <Qr text={setup.uri} />
                <div>
                  <p style={{ margin: '0 0 6px' }}>{t('totp.orSecret')}</p>
                  <code className="totp-secret">{group(setup.secret)}</code>
                  <div style={{ marginTop: 8 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(setup.secret)}>{copied ? t('totp.copied') : t('totp.copySecret')}</button>
                  </div>
                </div>
              </div>
            </li>
            <li>{t('totp.step3')}</li>
          </ol>
          <div className="form-group">
            <label htmlFor="login-totp-setup">{t('totp.code')}</label>
            <input id="login-totp-setup" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={6} placeholder="000000" autoComplete="one-time-code" autoFocus required />
          </div>
          <button type="submit" className="btn btn-lg" disabled={busy || code.length !== 6}>{t('totp.activate')}</button>
        </form>
      )}
      <div className="login-links">
        <button type="button" className="link-button" onClick={onBack}>{t('auth.otpBack')}</button>
      </div>
    </>
  );
}

export default KernelTotpLoginSetup;
